import React from 'react';
import { connect } from 'react-redux';
import Button from '@material-ui/core/Button';
import Icon from '@material-ui/core/Icon';
import { withTranslation, WithTranslation } from 'react-i18next';
import { Link as RouterLink, RouteComponentProps } from 'react-router-dom';
// Components
import App from '../../../components/app';
import ErrorMessage from '../../../components/error-message';
import EnhancedTable from '../../../components/table';
import { TooltipButton } from '../../../components/tooltip-button';
import MainLoading from '../../../components/main-loading';

// Modules
import { actions as billActions } from '../../../redux/reducers/bills';
import { ErrorPayload, selectors as errorSelectors } from '../../../redux/reducers/errors';
import { selectors as pendingSelectors } from '../../../redux/reducers/pendingOperations';
import { tableRequestPoints } from '../../../redux/reducers/constants';
import { BillStatus, CreditNoteType } from '../../../redux/reducers/api-types';
// Ressources
import withGlobalStyles from '../../../utils/global-styles';
import UserUtils, { AccountRight, RightRank } from '../../../utils/userUtils';
import { paths } from '../../paths';
import { Cell, createCell } from '../../../components/table/table-cell-creator';
import { FormatType, HeadCell } from '../../../local-types';

const queryString = require('query-string');

interface SimpleBillProps extends WithTranslation, RouteComponentProps {
    globalClasses: any;
    loadingRecoverPaidRow: any;
    loadingUnrecoverablePaidRow: any;
    loadingRetryPaiement: any;
    simpleBillsActionsError: ErrorPayload;
    setUnrecoverable: (id: string, callback?: React.EffectCallback) => any;
    reloadBill: (id: string, payload?, callback?: React.EffectCallback) => any;
    retryPaiement: (id: string, callback?: React.EffectCallback) => any;
}

class SimpleBillListPage extends React.Component<SimpleBillProps> {
    tableRef: any;
    filterParams: any;
    statusOptions: any[];

    isAdmin: boolean;
    canRetryPayment: boolean;

    constructor(props: SimpleBillProps) {
        super(props);
        this.filterParams = queryString.parse(this.props.location.search);
        this.isAdmin = UserUtils.checkRightsOrAdmin({ name: AccountRight.CREDIT_NOTE_MGMT, mode: RightRank.W });
        this.canRetryPayment = UserUtils.checkRightsOrAdmin({
            name: AccountRight.RIDER_BILLING_PYMT_RETRY,
            mode: RightRank.W,
        });

        const { t } = props;
        const options: string[] = [
            '',
            BillStatus.PENDING,
            BillStatus.PAYMENT_PENDING,
            BillStatus.PAYMENT_FAIL_SOFT,
            BillStatus.PAYMENT_FAIL_HARD,
            BillStatus.PAYMENT_MANUAL,
            BillStatus.PAID,
        ];
        this.statusOptions = options.map((o) => {
            return { value: o, label: t(o ? `status.${o}` : '') };
        });
    }

    handleUnrecoverable = (id) => (e) => {
        this.props.setUnrecoverable(id, () => {
            this.tableRef.reload();
        });
    };

    handleRefresh = (id) => (e) => {
        this.props.reloadBill(id, {}, () => {
            this.tableRef.reload();
        });
    };

    handleRetryPaiement = (id) => (e) => {
        this.props.retryPaiement(id, () => {
            this.tableRef.reload();
        });
    };

    // renderRecoverPaidLoader = (row, id): JSX.Element => {
    // 	const activeRow: boolean = this.props.loadingRecoverPaidRow?.id === row.id;
    // 	if (activeRow) {
    // 		return <MainLoading />;
    // 	}
    // 	return null;
    // };

    // renderUnrecoverableLoader = (row, id): JSX.Element => {
    // 	const activeRow = this.props.loadingUnrecoverablePaidRow?.id === row.id;

    // 	if (activeRow && id) {
    // 		return <MainLoading />;
    // 	}
    // 	return null;
    // };

    renderRetryPaiementLoader = (row, id): JSX.Element => {
        const activeRow = this.props.loadingRetryPaiement?.id === row.id;

        if (activeRow && id) {
            return <MainLoading />;
        }
        return null;
    };

    headerBuilder = (): HeadCell[] => {
        const { t, globalClasses } = this.props;
        const headers: HeadCell[] = [
            createCell(t, Cell.BILL_NUMBER),
            createCell(t, Cell.BILL_DOCUMENT_PATH),
            createCell(t, Cell.BILL_CREATED_AT),
            createCell(t, Cell.USER_ID),
            createCell(t, Cell.COMPANY_ID),
            createCell(t, Cell.RIDE_NUMBER),
            {
                id: 'onboard_payment',
                label: t('onboard_payment'),
                type: FormatType.Boolean,
                sortable: false,
                formatCell: function (row) {
                    if (row.payment_type !== 'onboard_payment') {
                        return t('common:false');
                    }
                    return t('common:true');
                },
            },
            {
                id: 'status',
                label: t('common:table.status'),
                type: FormatType.PickerMultiple,
                sortable: false,
                pickerOptions: this.statusOptions,
                formatCell: function (row) {
                    return t(`status.${row.status}`);
                },
            },
            createCell(t, Cell.AMOUNT_PRE_TAX),
            createCell(t, Cell.AMOUNT_WITH_TAX),
        ];

        if (this.isAdmin || this.canRetryPayment) {
            headers.push({
                id: 'credit',
                label: t('common:action'),
                filterable: false,
                formatCell: (row) => {
                    return (
                        <>
                            {this.isAdmin && (
                                <TooltipButton title={t('credit.add')}>
                                    <Button
                                        size="small"
                                        variant="contained"
                                        color="primary"
                                        style={{ marginRight: 8 }}
                                        component={RouterLink}
                                        to={`${paths.billsCreditNotesAdd}?bill_number=${row.bill_number}&id=${row.id}&type=${CreditNoteType.RIDE}`}
                                        className={globalClasses.btnAutoWidth}
                                    >
                                        <Icon fontSize="small">add</Icon>
                                    </Button>
                                </TooltipButton>
                            )}
                            {this.canRetryPayment &&
                                (row.status === BillStatus.PAYMENT_FAIL_HARD ||
                                    row.status === BillStatus.PAYMENT_FAIL_SOFT) && (
                                    <TooltipButton title={t('credit.retry')}>
                                        <Button
                                            size="small"
                                            variant="contained"
                                            color="primary"
                                            style={{ marginRight: 8 }}
                                            onClick={this.handleRetryPaiement(row.id)}
                                            className={globalClasses.btnAutoWidth}
                                        >
                                            <Icon fontSize="small">cached</Icon>
                                        </Button>
                                    </TooltipButton>
                                )}
                        </>
                    );
                },
                loaderCell: (row, id) => this.renderRetryPaiementLoader(row, id),
            });
        }
        return headers;
    };

    render() {
        const { t } = this.props;

        return (
            <>
                <App
                    needRights={{ name: AccountRight.RIDER_BILLING_MGMT }}
                    disableBreadcrumb
                    header={{
                        title: t('title_simple'),
                    }}
                >
                    <ErrorMessage payload={this.props.simpleBillsActionsError} />
                    <EnhancedTable
                        onRef={(ref) => (this.tableRef = ref)}
                        headers={this.headerBuilder()}
                        type={tableRequestPoints.BILLS_SIMPLE_BETA}
                        filterParams={this.filterParams}
                    />
                </App>
            </>
        );
    }
}

const SimpleBillListPageWithStyleAndTransaltion = withGlobalStyles(
    withTranslation(['bill', 'common'])(SimpleBillListPage),
);

const mapStateToProps = (state) => ({
    loadingRetryPaiement: pendingSelectors.loadingRetryPaiement(state),
    simpleBillsActionsError: errorSelectors.simpleBillsActionsError(state),
});

const mapDispatchToProps = {
    reloadBill: billActions.reloadBill,
    setUnrecoverable: billActions.setUnrecoverable,
    retryPaiement: billActions.retryPaiement,
};

export default connect(mapStateToProps, mapDispatchToProps)(SimpleBillListPageWithStyleAndTransaltion);
